import React, { useMemo, useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch, useFormState, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import NumberFormat from 'react-number-format';
import _isEqual from 'lodash/isEqual';

import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';
import ErrorMessage from 'components/ErrorMessage';
import CheckoutButton from './CheckoutButton';
import Input from 'components/Input';
import CheckboxWithLabel from 'components/Checkbox/WithLabel';
import Tooltip from 'components/Tooltip';

import schema, { FIELD_NAMES } from './DonationForm.schema';
import { donationForm as TEXT } from 'locales/en/cart';
import { priceFromAPI, priceForAPI } from 'utils/price';
import { CURRENCY_SYMBOL } from 'utils/transform';

import {
  updateCartProduct,
  editCartProductTemp,
  updateIsAnonymous,
} from 'redux/slices/cart';
import { usePrevious, useDebounce } from 'hooks';

const DonationForm = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { isUpdating } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const initialAmount = useMemo(
    () =>
      `${product.amount}` ||
      `${product?.productable?.amount_options[1].amount}`,
    [product]
  );
  const isCustomAmount = useMemo(
    () =>
      product?.productable?.amount_options.every(
        (item) => `${item.amount}` !== initialAmount
      ),
    [initialAmount, product?.productable?.amount_options]
  );

  const defaultValues = {
    amount: initialAmount,
    radio: isCustomAmount ? 'true' : 'false',
    custom_amount: isCustomAmount ? priceFromAPI(initialAmount) : '',
    user_pays_fees: true,
    message: '',
    is_anonymous: false,
  };

  const { register, handleSubmit, control, setValue, clearErrors } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { errors, isSubmitting } = useFormState({ control });
  const [radio, user_pays_fees, custom_amount, amount, is_anonymous] = useWatch(
    {
      control,
      name: [
        FIELD_NAMES.radio,
        FIELD_NAMES.user_pays_fees,
        FIELD_NAMES.custom_amount,
        FIELD_NAMES.amount,
        FIELD_NAMES.is_anonymous,
      ],
    }
  );
  const debouncedCustomAmount = useDebounce(custom_amount, 1000);
  const amountRegister = register(FIELD_NAMES.amount);
  const radioRegister = register(FIELD_NAMES.radio);

  const selectedAmount = useMemo(() => {
    if (radio === 'true') {
      return priceForAPI(debouncedCustomAmount);
    } else {
      return amount;
    }
  }, [amount, debouncedCustomAmount, radio]);
  const prevAmount = usePrevious(selectedAmount);
  const prevUserPaysFees = usePrevious(user_pays_fees);

  useEffect(() => {
    if (!product.amount) {
      dispatch(
        updateCartProduct({
          cartProductId: product.id,
          payload: { amount: selectedAmount },
          user_pays_fees,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      ((prevAmount !== null &&
        !!selectedAmount &&
        prevAmount !== selectedAmount) ||
        (prevUserPaysFees !== null && prevUserPaysFees !== user_pays_fees)) &&
      selectedAmount >= 300
    ) {
      dispatch(
        updateCartProduct({
          cartProductId: product.id,
          payload: { amount: selectedAmount },
          user_pays_fees,
        })
      );
    }
  }, [
    dispatch,
    prevAmount,
    prevUserPaysFees,
    product.amount,
    product.id,
    selectedAmount,
    user_pays_fees,
  ]);

  const onSubmit = async (data) => {
    dispatch(updateIsAnonymous(is_anonymous));
    if (!data.message) return dispatch(editCartProductTemp(null));
    return dispatch(
      updateCartProduct({
        cartProductId: product.id,
        payload: { comments: btoa(data.message) },
        clearEditingProduct: true,
      })
    );
  };

  return (
    <>
      <div
        className="flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2
          data-testid="page-title"
          className="text-javelin-gray-dark text-sm lg:font-bold lg:text-lg mb-1 lg:mb-6"
        >
          {TEXT.title}
        </h2>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        ) : (
          <ChevronDown className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        )}
      </div>
      <div
        className={`lg:block lg:h-full overflow-hidden lg:overflow-visible transition-height ease-in-out lg:mb-6 mb-2 ${
          isExpanded ? 'h-128' : 'h-0'
        }`}
      >
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-between overflow-x-visible px-0.5 lg:px-0"
        >
          <div
            data-testid="amounts"
            className="mx-0.5 lg:mx-0 flex flex-col mb-2"
          >
            {product.productable.amount_options.map((item, idx) => (
              <label
                htmlFor={item.amount}
                key={item.amount}
                className="lg:mb-3 mb-1"
              >
                <input
                  defaultChecked={`${item.amount}` === initialAmount}
                  id={item.amount}
                  ref={amountRegister.ref}
                  type="radio"
                  className="border-gray-300 focus:border-javelin-blue-dark focus:ring-transparent text-javelin-blue-dark p-1 border lg:h-5 lg:w-5"
                  value={`${item.amount}`}
                  name={amountRegister.name}
                  onChange={(e) => {
                    amountRegister.onChange(e);
                    setValue(FIELD_NAMES.radio, 'false');
                    setValue(FIELD_NAMES.custom_amount, '');
                    clearErrors(FIELD_NAMES.custom_amount);
                  }}
                />
                <span className="ml-3 text-sm text-javelin-gray-dark">{`${
                  CURRENCY_SYMBOL.EUR
                }${priceFromAPI(item.amount)}`}</span>
              </label>
            ))}
          </div>
          <label htmlFor={FIELD_NAMES.radio} className="mb-3 lg:w-fit-content">
            <input
              id={FIELD_NAMES.radio}
              value="true"
              name={radioRegister.name}
              ref={radioRegister.ref}
              onChange={(e) => {
                radioRegister.onChange(e);
                setValue(FIELD_NAMES.amount, '');
              }}
              type="radio"
              className="border-gray-300 focus:border-javelin-blue-dark focus:ring-transparent text-javelin-blue-dark p-1 border lg:h-5 lg:w-5"
            />
            <span className="ml-3 text-sm text-javelin-gray-dark">
              {TEXT.customAmount}
            </span>
            <Controller
              name={FIELD_NAMES.custom_amount}
              control={control}
              render={({ field: { onChange, ...field } }) => {
                return (
                  <NumberFormat
                    {...field}
                    id={FIELD_NAMES.custom_amount}
                    name={FIELD_NAMES.custom_amount}
                    prefix="€"
                    className={`input input-full-border py-3 mt-2 lg:ml-2 lg:mt-0 lg:w-28 lg:inline-block ${
                      !!errors?.[FIELD_NAMES.custom_amount]?.message
                        ? 'error'
                        : ''
                    }`}
                    onValueChange={(v) => {
                      onChange(v.value);
                    }}
                    onClick={() => {
                      if (radio === 'false') {
                        setValue(FIELD_NAMES.radio, 'true');
                        setValue(FIELD_NAMES.amount, '');
                      }
                    }}
                    placeholder="€"
                    thousandSeparator
                    fixedDecimalScale
                    decimalScale={2}
                  />
                );
              }}
            />
            {!!errors?.[FIELD_NAMES.custom_amount]?.message && (
              <ErrorMessage
                message={errors?.[FIELD_NAMES.custom_amount]?.message}
                className="text-sm"
              />
            )}
          </label>
          <Input
            label={TEXT.message}
            name={FIELD_NAMES.message}
            register={register}
            className="my-2 mx-0.5 lg:mx-0"
          />
          <CheckboxWithLabel
            label={
              <div>
                {TEXT.paysFees}
                <Tooltip
                  text={TEXT.paysFeesTooltip}
                  className="inline-block align-bottom relative"
                  contentClass="-left-48 lg:left-2 top-3 cursor-pointer p-5 w-80 text-javelin-gray-dark bg-white"
                />
              </div>
            }
            name={FIELD_NAMES.user_pays_fees}
            leftSideClassName="lg:w-0"
            register={register}
            checkboxClassName="text-javelin-blue-dark"
            className="w-full"
          />
          <CheckboxWithLabel
            label={TEXT.anonymous}
            leftSideClassName="lg:w-0"
            checkboxClassName="text-javelin-blue-dark"
            register={register}
            name={FIELD_NAMES.is_anonymous}
          />
          <CheckoutButton
            title={TEXT.submit}
            showSpinner={isSubmitting || isUpdating}
          />
        </form>
      </div>
    </>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  return _isEqual(prevProps, nextProps);
};

export default memo(DonationForm, propsAreEqual);
